/**
 * Trade icons, colors, and display names for NKY Connect UI.
 * Catalog slugs and copy must match `docs/cosmos-seeds/` (source of truth) and Cosmos ServiceTypes.
 * Icons, colors, enrolledBackground, and TRADE_THEME_ORDER are web-only — not stored in Cosmos.
 */
import PlumbingIcon from '@mui/icons-material/Plumbing';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import GrassIcon from '@mui/icons-material/Grass';
import HandymanIcon from '@mui/icons-material/Handyman';

/** @typedef {'catalog' | 'businessDefined'} TradeSubserviceMode */

/**
 * @typedef {Object} TradeTheme
 * @property {string} id
 * @property {string} name
 * @property {import('@mui/material').SvgIconComponent} icon
 * @property {string} color
 * @property {string} description
 * @property {TradeSubserviceMode} subserviceMode
 * @property {string} [enrolledBackground] Subtle card background when enrolled on Service Dashboard
 */

/** @type {Record<string, TradeTheme>} */
export const TRADE_THEMES = {
  plumbing: {
    id: 'plumbing',
    name: 'Plumbing',
    icon: PlumbingIcon,
    color: '#0d4efe',
    description: 'Drains, leaks, toilets, water heaters, fixtures, and disposals.',
    subserviceMode: 'catalog',
    enrolledBackground: '#fcfdff',
  },
  electrical: {
    id: 'electrical',
    name: 'Electrical',
    icon: ElectricalServicesIcon,
    color: '#f59e0b',
    description:
      'New installations and upgrades, troubleshooting and repairs, and electrical safety inspections for homes and businesses.',
    subserviceMode: 'catalog',
    enrolledBackground: '#fffdf8',
  },
  hvac: {
    id: 'hvac',
    name: 'HVAC',
    icon: AcUnitIcon,
    color: '#06b6d4',
    description:
      'Heating and cooling repair, maintenance, and installation, including thermostats and new equipment.',
    subserviceMode: 'catalog',
    enrolledBackground: '#f8fefe',
  },
  landscaping: {
    id: 'landscaping',
    name: 'Landscaping & Lawn Care',
    icon: GrassIcon,
    color: '#16a34a',
    description:
      'Lawn mowing and edging, lawn health treatments, tree and shrub work, yard cleanup, planting and beds, irrigation, grading and drainage, and outdoor hardscaping such as patios, walls, and lighting.',
    subserviceMode: 'catalog',
    enrolledBackground: '#f8fdf8',
  },
  other: {
    id: 'other',
    name: 'Custom Services',
    icon: HandymanIcon,
    color: '#475569',
    description: 'Services you define — title, description, optional tagline, and pricing.',
    subserviceMode: 'businessDefined',
    enrolledBackground: '#f8fafc',
  },
};

/** Customer-facing label for `other` in search/filter pickers (API slug stays `other`). */
export const OTHER_TRADE_PICKER_LABEL = 'Other';

/**
 * Label for trade pickers on customer surfaces (e.g. Our Partners filter, Smart (AI) search).
 * Business dashboard and enrolled-trade copy keep `TradeTheme.name` (e.g. Custom Services).
 * @param {string | null | undefined} tradeId
 */
export function getTradePickerLabel(tradeId) {
  if (tradeId === 'other') return OTHER_TRADE_PICKER_LABEL;
  const theme = getTradeTheme(tradeId);
  return theme?.name ?? (typeof tradeId === 'string' && tradeId.trim() ? tradeId : '—');
}

/** Default display order for trade pickers and lists. */
export const TRADE_THEME_ORDER = [
  'plumbing',
  'electrical',
  'hvac',
  'landscaping',
  'other',
];

/**
 * @param {string | null | undefined} tradeId
 * @returns {TradeTheme | null}
 */
export function getTradeTheme(tradeId) {
  if (!tradeId || typeof tradeId !== 'string') return null;
  return TRADE_THEMES[tradeId] ?? null;
}

/**
 * Fields merged onto catalog trade entries in `allTrades.js`.
 * @param {string} tradeId
 * @returns {TradeTheme | null}
 */
export function getTradeThemeFields(tradeId) {
  return getTradeTheme(tradeId);
}

/** @returns {TradeTheme[]} */
export function getTradeThemeList() {
  return TRADE_THEME_ORDER.map((id) => TRADE_THEMES[id]).filter(Boolean);
}

/**
 * @param {string} hex
 * @param {number} alpha
 */
export function hexToRgba(hex, alpha) {
  const normalized = String(hex || '').replace('#', '');
  if (normalized.length !== 6) {
    return `rgba(13, 78, 254, ${alpha})`;
  }
  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Inline styles for a trade icon chip (e.g. MUI `sx` on an icon wrapper).
 * @param {string | null | undefined} tradeId
 */
export function getTradeIconChipStyle(tradeId) {
  const theme = getTradeTheme(tradeId);
  if (!theme) {
    return { background: 'rgba(13, 78, 254, 0.12)', color: '#0d4efe' };
  }
  return {
    background: hexToRgba(theme.color, 0.12),
    color: theme.color,
  };
}

/**
 * CSS class name for Service Dashboard icon chips: `trade-color-{id}`.
 * @param {string | null | undefined} tradeId
 */
export function getTradeColorClassName(tradeId) {
  return tradeId ? `trade-color-${tradeId}` : 'trade-color-plumbing';
}

/**
 * Generates trade color rules for Service Dashboard styled-components.
 * @returns {string}
 */
export function buildTradeDashboardColorCss() {
  return TRADE_THEME_ORDER.map((id) => {
    const theme = TRADE_THEMES[id];
    if (!theme) return '';
    const border = hexToRgba(theme.color, 0.22);
    const chipBg = hexToRgba(theme.color, 0.12);
    const enrolledBg = theme.enrolledBackground || '#fcfdff';
    return `
  .trade-color-${id} { background: ${chipBg}; color: ${theme.color}; }
  .trade-card.enrolled.trade-${id} { border-color: ${border}; background: ${enrolledBg}; }
  .trade-${id} .subservice-toggle .toggle-on { color: ${theme.color}; }`;
  }).join('');
}
